import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';

import { cn } from '#/lib/utils';
import PrintInventoryStockLabelsButton from '#/modules/imsMgmt/inventoryStock/components/PrintInventoryStockLabelsButton';
import {
  canPrintStockAdjustmentLabel,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentStatus';
import {
  collectAdjustmentPrintInventoryStockIds,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/collectAdjustmentPrintInventoryStockIds';

import StockAdjustmentLinesTable from './StockAdjustmentLinesTable';

const StockAdjustmentDetailSection = ({
  detail,
  defaultOpen = true,
  embedded = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [selectedInventoryStockIds, setSelectedInventoryStockIds] = useState(() => new Set());
  const canPrintLabel = canPrintStockAdjustmentLabel(detail?.status);
  const adjustmentNo = detail?.adjustmentNo || '-';
  const lines = useMemo(() => detail?.lines ?? [], [detail?.lines]);
  const selectedIdsArray = useMemo(
    () => Array.from(selectedInventoryStockIds),
    [selectedInventoryStockIds],
  );
  const allInventoryStockIds = useMemo(
    () => collectAdjustmentPrintInventoryStockIds(lines),
    [lines],
  );

  const handleToggleLine = useCallback((inventoryStockId, checked) => {
    setSelectedInventoryStockIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(inventoryStockId);
      } else {
        next.delete(inventoryStockId);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback((checked) => {
    if (checked === true) {
      setSelectedInventoryStockIds(new Set(allInventoryStockIds));
      return;
    }
    setSelectedInventoryStockIds(new Set());
  }, [allInventoryStockIds]);

  const selectedPrintableCount = allInventoryStockIds.filter(
    (id) => selectedInventoryStockIds.has(id),
  ).length;
  const allSelected = allInventoryStockIds.length > 0
    && selectedPrintableCount === allInventoryStockIds.length;
  const someSelected = selectedPrintableCount > 0
    && selectedPrintableCount < allInventoryStockIds.length;
  const selectAllChecked = allSelected ? true : (someSelected ? 'indeterminate' : false);

  const linesTable = (
    <StockAdjustmentLinesTable
      lines={lines}
      status={detail?.status}
      canPrintLabel={canPrintLabel}
      selectedInventoryStockIds={selectedInventoryStockIds}
      selectAllChecked={selectAllChecked}
      selectAllDisabled={allInventoryStockIds.length === 0}
      onToggleLine={handleToggleLine}
      onToggleAll={handleToggleAll}
    />
  );

  const sectionHeader = (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-4 py-3',
        embedded && 'border-t border-slate-200',
        open && 'bg-slate-50',
        embedded && !open && 'rounded-b-2xl',
        !embedded && (open ? 'rounded-t-2xl' : 'rounded-2xl'),
      )}
    >
      <button
        type="button"
        className="
          flex min-w-0 flex-1 items-center gap-3 text-left transition-colors
          hover:text-primary-600
        "
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <ChevronDown
          size={18}
          className={cn(
            'shortcut_transition-transform shrink-0 origin-center text-gray-600',
            open ? 'rotate-0' : '-rotate-90',
          )}
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-800">調整明細</h2>
          <p className="truncate text-sm text-gray-500">{adjustmentNo}</p>
        </div>
      </button>
      {canPrintLabel ? (
        <div
          className="shrink-0"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          <PrintInventoryStockLabelsButton
            selectedInventoryStockIds={selectedIdsArray}
            allInventoryStockIds={allInventoryStockIds}
            defaultItemId="all"
          />
        </div>
      ) : null}
    </div>
  );

  const sectionBody = (
    <div
      className={cn(
        'grid shortcut_transition-disclosure',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
      aria-hidden={!open}
    >
      <div className="min-h-0 overflow-hidden rounded-b-2xl">
        <div className={cn('stock-receipt-detail-section__scroll')}>
          {linesTable}
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <>
        {sectionHeader}
        {sectionBody}
      </>
    );
  }

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface">
        {sectionHeader}
        {sectionBody}
      </div>
    </div>
  );
};

StockAdjustmentDetailSection.propTypes = {
  detail: PropTypes.shape({
    adjustmentNo: PropTypes.string,
    status: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  defaultOpen: PropTypes.bool,
  embedded: PropTypes.bool,
};

export default StockAdjustmentDetailSection;
